import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, X, Sparkles, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const TOUR_STEPS = [
  { id: "welcome", target: null, title: "¡Bienvenido(a) a tu consultorio digital!", body: "En menos de 2 minutos te mostramos cómo está organizada la plataforma para que empieces a trabajar hoy mismo. Puedes repetir esta guía cuando quieras desde el botón \"Guía\" de la barra superior." },
  { id: "sidebar", target: '[data-testid="sidebar"]', placement: "right", title: "Menú principal", body: "Aquí están todos los módulos. Pasa el cursor sobre la barra para ver los nombres completos. Todo lo que hagas se organiza en estas secciones." },
  { id: "dashboard", target: '[data-testid="nav-dashboard"]', placement: "right", title: "Resumen del día", body: "Citas de hoy, ingresos, pacientes atendidos y alertas importantes (por ejemplo, si la caja aún no está abierta)." },
  { id: "agenda", target: '[data-testid="nav-agenda"]', placement: "right", title: "Agenda de citas", body: "Crea citas por día, semana o mes. Cada cita pasa por estados: Pendiente → Confirmada → En atención → Finalizada. Al finalizar registras la evolución y el cobro en un solo paso." },
  { id: "pacientes", target: '[data-testid="nav-pacientes"]', placement: "right", title: "Pacientes e historia clínica", body: "Ficha completa de cada paciente: anamnesis, evoluciones, odontograma, periodontograma, plan de tratamiento, presupuestos, recibos, radiografías, recetas y consentimientos con plantillas listas. Todo exportable a PDF." },
  { id: "caja", target: '[data-testid="nav-caja"]', placement: "right", title: "Caja diaria", body: "Importante: abre la caja al iniciar el día para poder registrar cobros. Aquí ves ingresos por medio de pago, egresos, cierres y los recibos generados." },
  { id: "contabilidad", target: '[data-testid="nav-contabilidad"]', placement: "right", title: "Contabilidad y reportes", body: "Resumen de ingresos y gastos por periodo, cuentas por cobrar, cortesías y reportes para tu contador." },
  { id: "ajustes", target: '[data-testid="nav-ajustes"]', placement: "right", title: "Ajustes del consultorio", body: "Configura usuarios y roles, el catálogo de tratamientos con precios, horarios, y la marca de tu clínica (logo y colores) que aparecerá en los PDF." },
  { id: "tabs", target: '[data-testid="tabs-bar"]', placement: "bottom", title: "Pestañas persistentes", body: "Cada módulo o paciente que abres se queda en una pestaña, como en un navegador. Puedes trabajar con varios pacientes a la vez sin perder nada." },
  { id: "search", target: '[data-testid="cmd-k-trigger"]', placement: "bottom", title: "Búsqueda rápida", body: "Busca pacientes o citas desde cualquier pantalla. Atajo: Ctrl/Cmd + K." },
  { id: "signature", target: '[data-testid="my-signature-btn"]', placement: "bottom", title: "Tu firma", body: "Dibuja o sube tu firma una sola vez: se incluirá automáticamente en evoluciones, presupuestos, recibos y cierres de caja que generes." },
  { id: "done", target: null, title: "¡Todo listo!", body: "Te recomendamos empezar así:", checklist: ["Abrir la caja del día (Caja → Abrir caja)", "Revisar tratamientos y precios en Ajustes", "Registrar tu primer paciente", "Agendar y finalizar una cita de prueba"] },
];

const PAD = 8;

export default function GuidedTour({ open, onClose }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const steps = useMemo(() => {
    if (!open) return TOUR_STEPS;
    return TOUR_STEPS.filter((s) => {
      if (!s.target) return true;
      const el = document.querySelector(s.target);
      return el && el.getClientRects().length > 0;
    });
  }, [open]);

  const step = steps[idx];
  const total = steps.length;

  const measure = useCallback(() => {
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
  }, [step]);

  useEffect(() => { if (open) setIdx(0); }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [open, measure]);

  const finish = useCallback((completed) => onClose && onClose(completed), [onClose]);
  const next = useCallback(() => (idx < total - 1 ? setIdx(idx + 1) : finish(true)), [idx, total, finish]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish(false);
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, finish]);

  if (!open || !step) return null;

  const vw = window.innerWidth, vh = window.innerHeight;
  const CARD_W = Math.min(380, vw - 32);
  let cardStyle;
  if (!rect) {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: CARD_W };
  } else if (step.placement === "right" && rect.left + rect.width + CARD_W + 16 < vw) {
    cardStyle = { top: Math.max(16, Math.min(rect.top, vh - 320)), left: rect.left + rect.width + 12, width: CARD_W };
  } else {
    const left = Math.max(16, Math.min(rect.left, vw - CARD_W - 16));
    cardStyle = { top: rect.top + rect.height + 12, left, width: CARD_W };
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour" role="dialog" aria-modal="true">
      {rect ? (
        <div
          className="absolute rounded-sm transition-all duration-300 ease-out pointer-events-none"
          style={{ ...rect, boxShadow: "0 0 0 9999px rgba(10,20,15,0.72)", outline: "2px solid var(--brand-primary, #1B4332)" }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(10,20,15,0.72)]" />
      )}
      <div className="absolute inset-0" onClick={() => finish(false)} />

      <div className="absolute bg-card text-foreground border border-border rounded-sm shadow-2xl p-5 transition-all duration-300 ease-out" style={cardStyle} data-testid="tour-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="label-eyebrow text-primary flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Guía · Paso {idx + 1} de {total}</div>
          <button onClick={() => finish(false)} className="p-1 -mr-1 -mt-1 rounded-sm text-muted-foreground hover:bg-muted" aria-label="Saltar guía" data-testid="tour-skip"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-display font-bold text-xl tracking-tight leading-tight">{step.title}</h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.body}</p>
        {step.checklist && (
          <ul className="mt-3 space-y-1.5">
            {step.checklist.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />{c}</li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between mt-5 gap-3">
          <div className="flex gap-1">
            {steps.map((s, i) => <span key={s.id} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-primary" : "w-1.5 bg-border"}`} />)}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button onClick={prev} className="h-9 px-3 rounded-sm border border-border text-xs font-bold uppercase tracking-widest hover:bg-muted flex items-center gap-1" data-testid="tour-prev"><ArrowLeft className="w-3.5 h-3.5" /> Atrás</button>
            )}
            {step.id === "done" && (
              <button onClick={() => { finish(true); navigate("/caja"); }} className="h-9 px-3 rounded-sm border border-primary text-primary text-xs font-bold uppercase tracking-widest hover:bg-primary/10 whitespace-nowrap" data-testid="tour-go-caja">Ir a Caja</button>
            )}
            <button onClick={next} className="btn-primary !h-9 text-xs uppercase tracking-widest flex items-center gap-1" data-testid="tour-next">
              {idx === total - 1 ? "Finalizar" : "Siguiente"} {idx < total - 1 && <ArrowRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
